import { ColDef, GridReadyEvent, IRowNode, RowSelectedEvent } from '@ag-grid-community/core';
import { Box, Flex, Input, useBreakpointValue } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VisitTypeDto, api } from '../../../lib/api';
import { InputGroup } from '@/components/ui/input-group';
import { LinkButton } from '@/components/ui/link-button';
import { Plus, Search } from 'lucide-react';
import { ResponsiveGrid } from '@/components/ui/responsive-grid';

const defaultColumnDef: ColDef<VisitTypeDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
};

const onGridReady = (params: GridReadyEvent) => {
    params.api.setFilterModel({
        status: {
            value: 'active',
        },
    });
};

export const VisitTypesPage = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('');

    const isMobile = useBreakpointValue({ base: true, md: false, lg: false });
    const isTablet = useBreakpointValue({ base: false, md: true, lg: false });

    const query = useQuery<VisitTypeDto[]>({
        queryKey: ['musa', 'settings', 'visitTypes'],
        queryFn: async () => {
            const { data } = await api.visitType.findAll();
            return data;
        },
    });

    const colDefs = useMemo<ColDef<VisitTypeDto>[]>(() => {
        if (isMobile) {
            return [
                {
                    field: 'title',
                    headerName: 'Title',
                    filter: 'agTextColumnFilter',
                    pinned: 'left',
                    suppressAutoSize: true,
                    initialWidth: 150,
                },
                {
                    field: 'type',
                    headerName: 'Service Type',
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'discipline',
                    headerName: 'Discipline',
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    filter: 'agTextColumnFilter',
                },
            ];
        }

        if (isTablet) {
            return [
                {
                    field: 'title',
                    headerName: 'Title',
                    filter: 'agTextColumnFilter',
                    pinned: 'left',
                    suppressAutoSize: true,
                    initialWidth: 180,
                    minWidth: 150,
                },
                {
                    field: 'type',
                    headerName: 'Service Type',
                    filter: 'agTextColumnFilter',
                    flex: 1,
                    minWidth: 120,
                },
                {
                    field: 'discipline',
                    headerName: 'Discipline',
                    filter: 'agTextColumnFilter',
                    flex: 1,
                    minWidth: 100,
                },
                {
                    field: 'employeeClass',
                    headerName: 'Employee Class',
                    filter: 'agTextColumnFilter',
                    flex: 1,
                    minWidth: 120,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    filter: 'agTextColumnFilter',
                    width: 100,
                    minWidth: 80,
                },
            ];
        }

        return [
            { field: 'title', flex: 1, filter: 'agTextColumnFilter', suppressHeaderFilterButton: true, resizable: false },
            {
                field: 'type',
                headerName: 'Service Type',
                flex: 1,
                filter: 'agTextColumnFilter',
                resizable: false,
            },
            {
                field: 'discipline',
                flex: 1,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: false,
                resizable: false,
            },
            {
                field: 'employeeClass',
                headerName: 'Title',
                flex: 1,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: false,
                resizable: false,
            },
            { field: 'status', flex: 1, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: false },
            { flex: 2, resizable: false },
        ];
    }, [isMobile, isTablet]);

    const handleAdd = async () => {
        await queryClient.invalidateQueries({ queryKey: ['musa', 'settings', 'visitTypes'] });
        navigate('/settings/visit-type/create');
    };

    const handleSelect = (event: RowSelectedEvent<VisitTypeDto>) => {
        if (event.node.isSelected()) {
            navigate(`/settings/visit-type/${event.data?.id}/edit`);
        }
    };

    const doesExternalFilterPass = useCallback(
        (node: IRowNode<VisitTypeDto>): boolean => {
            if (node.data) {
                return node.data.title.toLowerCase().includes(filter.toLowerCase());
            }
            return true;
        },
        [filter],
    );

    const isExternalFilterPresent = useCallback((): boolean => {
        return !!filter;
    }, [filter]);

    return (
        <Flex px={{ base: '16px', md: 0 }} pt="16px" h="100%" flexDirection="column">
            <Flex alignItems="center" justifyContent="space-between" pb="12px" px={{ base: 0, md: '16px' }} gap="8px">
                <InputGroup w="296px" startElement={<Search />}>
                    <Input
                        type="search"
                        placeholder="Find"
                        h="32px"
                        value={filter}
                        onInput={(event) => {
                            setFilter((event.target as HTMLInputElement).value);
                        }}
                    />
                </InputGroup>
                <Flex>
                    <LinkButton size="xs" to="/settings/visit-type/create" textDecoration="none" onClick={handleAdd}>
                        <Plus /> Add Visit Type
                    </LinkButton>
                </Flex>
            </Flex>
            <Box position="relative" w="100%" h="100%" pb="24px">
                <ResponsiveGrid
                    onGridReady={onGridReady}
                    key={isMobile ? 'mobile-grid' : isTablet ? 'tablet-grid' : 'desktop-grid'}
                    rowSelection="single"
                    columnDefs={colDefs}
                    rowData={query.data}
                    suppressCellFocus
                    defaultColDef={defaultColumnDef}
                    autoSizeStrategy={
                        isMobile
                            ? { type: 'fitCellContents' as const, colIds: ['type', 'discipline', 'status'], skipHeader: true }
                            : isTablet
                              ? {
                                    type: 'fitCellContents' as const,
                                    colIds: ['type', 'discipline', 'employeeClass', 'status'],
                                    skipHeader: true,
                                }
                              : { type: 'fitGridWidth' as const }
                    }
                    onRowSelected={handleSelect}
                    doesExternalFilterPass={doesExternalFilterPass}
                    isExternalFilterPresent={isExternalFilterPresent}
                />
            </Box>
        </Flex>
    );
};
